/**
 * useUnsavedWork — keyed dirty-work registry.
 *
 * Module-scope singleton: every caller shares the same registry.
 *
 * registerDirty(key)  — mark a surface (e.g. 'pdv-cart', 'os-form') as having
 *                       in-progress work that would be lost on a hard nav.
 * clearDirty(key)     — mark the surface clean (form saved / modal closed).
 * hasDirty()          — returns true if ANY key is currently dirty.
 *
 * Wiring surfaces (PDV/OS forms etc.) is OUT OF SCOPE for F10 — this
 * composable ships the guard seam. Surfaces opt in by calling registerDirty /
 * clearDirty at the appropriate lifecycle points.
 *
 * The httpService subscription_blocked interceptor checks hasDirty() before
 * performing the hard-nav redirect to /minhas-assinaturas: dirty → confirm
 * dialog; clean → immediate redirect.
 */

// Module-scope singleton store — no Vue reactivity needed (boolean query only).
const _dirtyKeys = new Set<string>()

export function useUnsavedWork() {
  /** Mark a named surface as having unsaved work. */
  function registerDirty(key: string): void {
    _dirtyKeys.add(key)
  }

  /** Mark a named surface as clean. */
  function clearDirty(key: string): void {
    _dirtyKeys.delete(key)
  }

  /** Returns true if any registered surface currently has unsaved work. */
  function hasDirty(): boolean {
    return _dirtyKeys.size > 0
  }

  /** Reset all dirty state — use in afterEach to avoid cross-test leakage. */
  function _reset(): void {
    _dirtyKeys.clear()
  }

  return { registerDirty, clearDirty, hasDirty, _reset }
}
